import { useTranslation } from "@redotech/redo-i18n";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo, useContext } from "react";
import { PrinterContext } from "./printer-context";

interface RedoConnectWarningProps {
  computerId: number | undefined;
  compact?: boolean;
}

export const RedoConnectWarning = memo(function RedoConnectWarning({
  computerId,
  compact = false,
}: RedoConnectWarningProps) {
  const { t } = useTranslation();
  const { computers, multipleConnectionComputerIds } =
    useContext(PrinterContext);
  const computer = computers.find((candidate) => candidate.id === computerId);

  if (!computer) {
    return null;
  }

  const hasMultipleConnections = multipleConnectionComputerIds.includes(
    computer.id,
  );
  if (!hasMultipleConnections) {
    return null;
  }

  const title = t("Check Redo Connect");
  const description = t(
    "Multiple Redo Connect connections are appearing as {{computerName}}. Make sure each computer has a unique name and only one copy of Redo Connect running. After making changes, restart the affected computers. If the warning continues, contact Redo support.",
    { computerName: computer.name },
  );

  if (compact) {
    return (
      <Tooltip leftAlign title={description}>
        <Flex align="center" gap="xs" justify="center">
          <RedoIcon color="warning" Icon={AlertTriangleIcon} size={16} />
          <Text fontSize="xs" textColor="warning">
            {title}
          </Text>
        </Flex>
      </Tooltip>
    );
  }

  return (
    <RedoAlert
      color="warning"
      description={description}
      fullWidth
      size="sm"
      title={title}
    />
  );
});
